import { PopoverPlacement } from "@idui/react-popover";
import { AutoAwesomeMotion, MultipleStop, RvHookup } from "@mui/icons-material";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  CourseIndicatorIcon,
  CourseIndicatorTooltip,
  FramerTooltip,
  RideStatusFilledIndicator,
} from "@ryde/controls";
import { useRideEmptyStationsAllowed } from "@ryde/redux";
import {
  DirectionEnum,
  editFormDataAF,
  EditFormMode,
  getRideRouteType,
  IRideDto,
  NO_VALUE_DISPLAY,
  onActivateKeyDown,
  RideCrudModeKeyTuple,
  RideStationPanelMode,
  useDrawers,
} from "@ryde/shared";

import { TabsBottomInfoItemStyled } from "../../../../right";
import { RideHubInfoIndicator } from "./hub-info";
import { RideHeaderDirection } from "./ride-header-direction";
import { HeaderLine1Styled } from "./ride-header.styled";
import { RidePassengersSeatsInfo } from "./ride-passengers-seats-info";

export const RideHeaderInfosLine1: FC = () => {
  const { t } = useTranslation();
  const rideDto = useRecoilValue<Partial<IRideDto> | undefined>(
    editFormDataAF(RideCrudModeKeyTuple),
  );
  const { openCourseEditFormDrawer } = useDrawers();

  const { isRideWithEmptyStationsAllowed, isRideHasIndependentDayAdjustment } =
    useRideEmptyStationsAllowed(RideStationPanelMode.CRUD_TAB, rideDto);

  const handleCourseClick = (): void => {
    const courseId = rideDto?.courseId;

    if (courseId) {
      openCourseEditFormDrawer({
        id: courseId,
        mode: EditFormMode.UnboundDrawer,
      });
    }
  };

  const hasStationsPruned = Boolean(rideDto?.metadata?.wasStationsPruned);
  const hasCourse = !!rideDto?.courseId;
  const hasStatus = !!rideDto?.status;

  const headerTitle = useMemo(() => {
    return hasCourse ? t("general.FIXED_COURSE") : t("general.DYNAMIC_RIDE");
  }, [hasCourse, t]);

  if (!rideDto) {
    return null;
  }

  return (
    <HeaderLine1Styled>
      <FramerTooltip
        content={t("ride.formLabel.rideTypeId")}
        placement={PopoverPlacement.bottom}
      >
        <h2 data-e2e="ride-sidebar-header">
          {headerTitle}
          {/*hack that helps to select only id without `title` on double click*/}
          &#8203;
          <span className="ride-id">{rideDto.id ?? NO_VALUE_DISPLAY}</span>
        </h2>
      </FramerTooltip>

      <TabsBottomInfoItemStyled>
        <RideHeaderDirection
          direction={rideDto.direction || DirectionEnum.PickUp}
        />
      </TabsBottomInfoItemStyled>

      {hasStatus && (
        <TabsBottomInfoItemStyled>
          <FramerTooltip
            placement={PopoverPlacement.bottom}
            content={t("ride.formLabel.status")}
          >
            <RideStatusFilledIndicator status={rideDto.status} />
          </FramerTooltip>
        </TabsBottomInfoItemStyled>
      )}

      {hasCourse && (
        <TabsBottomInfoItemStyled>
          <FramerTooltip
            placement={PopoverPlacement.bottom}
            content={<CourseIndicatorTooltip rideDto={rideDto} />}
          >
            <div className="item__content truncate-text">
              <label
                data-e2e="ride-course-info"
                className="course pointer"
                role="button"
                tabIndex={0}
                onClick={handleCourseClick}
                onKeyDown={onActivateKeyDown(handleCourseClick)}
              >
                <CourseIndicatorIcon routeType={getRideRouteType(rideDto)} />
                {rideDto.courseId} {rideDto.courseName}
              </label>
            </div>
          </FramerTooltip>
        </TabsBottomInfoItemStyled>
      )}

      {hasStationsPruned && (
        <TabsBottomInfoItemStyled>
          <FramerTooltip
            content={t("ride.formLabel.wasStationsPruned")}
            placement={PopoverPlacement.bottom}
          >
            <RvHookup className="independent_day_adjustment_indicator" />
          </FramerTooltip>
        </TabsBottomInfoItemStyled>
      )}

      {isRideWithEmptyStationsAllowed && (
        <TabsBottomInfoItemStyled>
          <FramerTooltip
            content={t("rideType.formLabel.isRideWithEmptyStationsAllowed")}
            placement={PopoverPlacement.bottom}
          >
            <AutoAwesomeMotion className="empty_station" />
          </FramerTooltip>
        </TabsBottomInfoItemStyled>
      )}

      {isRideHasIndependentDayAdjustment && (
        <TabsBottomInfoItemStyled>
          <FramerTooltip
            content={t("selectListLabel.IndependentDayAdjustment")}
            placement={PopoverPlacement.bottom}
          >
            <MultipleStop className="independent_day_adjustment_indicator" />
          </FramerTooltip>
        </TabsBottomInfoItemStyled>
      )}
      {!hasCourse && <RidePassengersSeatsInfo rideDto={rideDto} />}

      <RideHubInfoIndicator />
    </HeaderLine1Styled>
  );
};
